import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CancelOutlined as CancelIcon,
  RemoveCircleOutline as PalletRemoveIcon,
  Inventory2Outlined as PalletAddIcon,
  PrintOutlined as PrintIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  TaskAlt as ConfirmIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import {
  fulfillmentApiBase,
  fulfillmentListPage,
  withAccountBody,
  withAccountParams,
} from '../../utils/fulfillmentApi.mjs';
import usePrinter from '../../hooks/usePrinter';
import { statusChipSx } from '../../pages/fulfillmentUi.mjs';
import ConfirmDialog from './ConfirmDialog';
import FulfillmentStatusChip from './FulfillmentStatusChip';
import FulfillmentProductThumb from './FulfillmentProductThumb';
import WbSupplyShippingFields, {
  normalizeShippingForm,
  shippingConfirmBody,
  shippingFormReady,
} from './WbSupplyShippingFields.jsx';

const PAGE_SIZE_KEY = 'fulfillment_rows_per_page';
const TABLE_SX = {
  minWidth: 860,
  '& .MuiTableCell-body': { py: 0.625 },
  '& .MuiChip-root': { height: 22 },
  '& .MuiChip-label': { px: 0.875 },
};

const STATUS_FILTERS = [
  { value: '', label: 'Все' },
  { value: 'open', label: 'Открытые' },
  { value: 'confirming', label: 'Подтверждение' },
  { value: 'partial', label: 'Частичные' },
  { value: 'failed', label: 'Ошибки' },
  { value: 'completed', label: 'Завершённые' },
  { value: 'cancelled', label: 'Отменённые' },
];

const COLUMNS = [
  { key: '__view', label: '' },
  { key: 'code', label: 'Сессия', sortable: true },
  { key: 'stats', label: 'Палеты / заказы' },
  { key: 'vehicle_label', label: 'Машина', sortable: true },
  { key: 'status', label: 'Статус', sortable: true },
  { key: '__actions', label: 'Действия', align: 'right' },
];

function initialPageSize() {
  const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return [25, 50, 100, 200].includes(saved) ? saved : 50;
}

function departedOrderLine(row) {
  const id = row?.posting_number ?? row?.yandex_order_id ?? row?.wb_order_id ?? '—';
  const article = row?.article ? ` · ${row.article}` : '';
  const reason = row?.reason || 'выбыл';
  return `${id}${article} — ${reason}`;
}

function formatApiError(error) {
  const data = error?.response?.data;
  const base = data?.message || error.message || 'Ошибка';
  const departed = data?.details?.departed_orders;
  if (!Array.isArray(departed) || !departed.length) return base;
  const lines = departed.slice(0, 12).map((row) => {
    const id = row.posting_number ?? row.wb_order_id ?? '—';
    const article = row.article ? ` · ${row.article}` : '';
    const reason = row.reason || 'выбыл';
    return `${id}${article} — ${reason}`;
  });
  const more = departed.length > 12 ? `\n…и ещё ${departed.length - 12}` : '';
  return `${base}\n${lines.join('\n')}${more}`;
}

function canRetrySession(row) {
  if (!row || row.ms_background) return false;
  if (row.status === 'confirming') return true;
  if (['partial', 'failed'].includes(row.status)) return true;
  if (row.status === 'completed' && Number(row.stats?.ms_pending || 0) > 0) return true;
  return false;
}

function msProgressPercent(row) {
  const total = Number(row?.stats?.orders || 0);
  if (!total) return 0;
  const pending = Number(row?.stats?.ms_pending || 0);
  return Math.max(0, Math.min(100, Math.round(((total - pending) / total) * 100)));
}

function sessionPalletCount(row) {
  if (!row) return 0;
  if (row.stats?.pallets != null) return Number(row.stats.pallets) || 0;
  if (row.pallets_count != null) return Number(row.pallets_count) || 0;
  if (Array.isArray(row.pallets)) return row.pallets.length;
  return 0;
}

function canConfirmSession(row, { isOzon = false, isYandex = false, isLocalWarehouse = false } = {}) {
  if (!row) return false;
  if (isLocalWarehouse || isOzon || isYandex) {
    if (!['open', 'partial', 'failed'].includes(row.status)) return false;
    if (isYandex) return Boolean(row.warehouse_id) || sessionPalletCount(row) > 0;
    return Boolean(row.delivery_method_id) || sessionPalletCount(row) > 0;
  }
  return row.status === 'open' && Number(row.stats?.pallets || 0) > 0;
}

/** Поставка, в которую уехала палета сессии (WB; у Ozon/Yandex поставок нет). */
function palletSupply(session, link) {
  if (!link?.session_supply_id) return null;
  return (session?.supplies || [])
    .find((s) => Number(s.id) === Number(link.session_supply_id)) || null;
}

/**
 * WB (планы 07/09): отмена из partial, пока ни одна поставка не ушла в WB.
 * Ozon/Yandex: из failed; если перевозка/акт уже в МП — бэкенд отвечает 409 с текстом.
 * Их partial не показываем: у Ozon там перевозка всегда есть, у Yandex partial не бывает.
 */
function canCancelSession(row, { isLocalWarehouse = false } = {}) {
  if (!row) return false;
  if (row.status === 'open') return true;
  if (isLocalWarehouse) return row.status === 'failed';
  if (row.status !== 'partial') return false;
  return !(row.supplies || []).some((s) => s.transfer_done);
}

/**
 * Снять палету можно из открытой сессии; из partial — только у WB (см. removePallet).
 * У Ozon partial бывает только после создания перевозки (палеты уже shipped),
 * у Yandex partial не пишется — бэкенд отвечает 409.
 */
function canRemovePallets(row, { isLocalWarehouse = false } = {}) {
  if (!row) return false;
  if (row.status === 'open') return true;
  return row.status === 'partial' && !isLocalWarehouse;
}

export default function LoadingSessionsPanel({
  accountId = null,
  marketplace = 'wb',
  canConfirm = false,
  writeStateDryRun = false,
}) {
  const isOzon = marketplace === 'ozon';
  const isYandex = marketplace === 'yandex';
  const isLocalWarehouse = isOzon || isYandex;
  const apiBase = fulfillmentApiBase(marketplace);
  const printer = usePrinter();
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sort, setSort] = useState({ key: 'id', dir: 'desc' });
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [qrBusyId, setQrBusyId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [departedPrompt, setDepartedPrompt] = useState(null);
  const [containersCount, setContainersCount] = useState('');
  const [containersError, setContainersError] = useState('');
  const [shippingForm, setShippingForm] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [vehicleLabel, setVehicleLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [addPalletsOpen, setAddPalletsOpen] = useState(false);
  const [addCandidates, setAddCandidates] = useState([]);
  const [addSelected, setAddSelected] = useState(() => new Set());
  const [addLoading, setAddLoading] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');

  const load = useCallback(async () => {
    if (isLocalWarehouse && !accountId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/loading-sessions`, {
        params: withAccountParams(marketplace, accountId, {
          page: fulfillmentListPage(marketplace, page),
          page_size: rowsPerPage,
          sort_key: sort.key,
          sort_dir: sort.dir,
          ...(statusFilter ? { status: statusFilter } : {}),
        }),
      });
      setItems(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, isLocalWarehouse, marketplace, page, rowsPerPage, sort, statusFilter]);

  useEffect(() => { setPage(0); }, [accountId, marketplace]);
  useEffect(() => { load(); }, [load]);

  const watchingMs = Boolean(
    !isLocalWarehouse
    && (detail?.ms_background || items.some((row) => row.ms_background)),
  );
  useEffect(() => {
    if (!watchingMs) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${apiBase}/loading-sessions`, {
          silent: true,
          params: withAccountParams(marketplace, accountId, {
            page: fulfillmentListPage(marketplace, page),
            page_size: rowsPerPage,
            sort_key: sort.key,
            sort_dir: sort.dir,
            ...(statusFilter ? { status: statusFilter } : {}),
          }),
        });
        if (cancelled) return;
        setItems(data?.items || []);
        setTotal(Number(data?.total) || 0);
        const detailId = detail?.id;
        if (!detailId) return;
        const next = (data?.items || []).find((row) => row.id === detailId);
        if (next) {
          setDetail(next);
          return;
        }
        const { data: fresh } = await axios.get(`${apiBase}/loading-sessions/${detailId}`, {
          silent: true,
          params: withAccountParams(marketplace, accountId),
        });
        if (!cancelled) setDetail(fresh?.session || fresh);
      } catch {
        /* silent poll */
      }
    };
    const timer = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [watchingMs, apiBase, marketplace, accountId, page, rowsPerPage, sort, statusFilter, detail?.id]);

  const handleSort = (key) => {
    setPage(0);
    setSort((old) => ({
      key,
      dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openDetail = async (session) => {
    setBusyId(session.id);
    try {
      const { data } = await axios.get(`${apiBase}/loading-sessions/${session.id}`, {
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  /** Ozon/Yandex remove-pallet и cancel не возвращают сериализованную сессию. */
  const reloadDetail = async (sessionId) => {
    try {
      const { data } = await axios.get(`${apiBase}/loading-sessions/${sessionId}`, {
        silent: true,
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(data?.session || data);
    } catch {
      setDetail(null);
    }
  };

  const createSession = async () => {
    setCreating(true);
    setError('');
    setNotice('');
    try {
      const body = isLocalWarehouse
        ? withAccountBody(marketplace, accountId)
        : { vehicle_label: vehicleLabel.trim() || undefined };
      const { data } = await axios.post(`${apiBase}/loading-sessions`, body);
      const session = data?.session || data;
      setCreateOpen(false);
      setVehicleLabel('');
      setNotice(`Сессия ${session?.code || ''} создана. Добавьте палеты здесь или сканом на ТСД.`);
      setDetail(session);
      setStatusFilter('open');
      setPage(0);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setCreating(false);
    }
  };

  const closeConfirm = () => {
    if (busyId === confirmTarget?.session?.id) return;
    setConfirmTarget(null);
    setDepartedPrompt(null);
    setShippingForm(null);
    setShippingError('');
    setContainersCount('');
    setContainersError('');
  };

  const loadShippingOptions = async (sessionId, city, preserveForm) => {
    setShippingLoading(true);
    setShippingError('');
    try {
      const { data } = await axios.get(
        `${apiBase}/loading-sessions/${sessionId}/shipping-options`,
        { params: withAccountParams(marketplace, accountId, city ? { city } : {}) },
      );
      setShippingForm(normalizeShippingForm(data, { preserve: preserveForm }));
    } catch (requestError) {
      setShippingForm(null);
      setShippingError(formatApiError(requestError));
    } finally {
      setShippingLoading(false);
    }
  };

  useEffect(() => {
    const sessionId = confirmTarget?.session?.id;
    if (!sessionId || isLocalWarehouse) {
      if (!sessionId) {
        setShippingForm(null);
        setShippingError('');
      }
      return undefined;
    }
    loadShippingOptions(sessionId);
    return undefined;
  }, [confirmTarget?.session?.id, confirmTarget?.retry, isLocalWarehouse]);

  const runConfirm = async (session, retry = false, { acknowledgeDeparted = false } = {}) => {
    if (isLocalWarehouse && retry) return;
    if (!isLocalWarehouse && !shippingFormReady(shippingForm)) return;
    setBusyId(session.id);
    setError('');
    setNotice('');
    setContainersError('');
    try {
      const path = retry
        ? `${apiBase}/loading-sessions/${session.id}/retry`
        : `${apiBase}/loading-sessions/${session.id}/confirm`;
      const shipping = !isLocalWarehouse && shippingForm && !shippingForm.skip_shipping
        ? shippingConfirmBody(shippingForm)
        : undefined;
      const { data } = await axios.post(
        path,
        withAccountBody(marketplace, accountId, {
          printer_uid: printerUid || undefined,
          ...(shipping ? { shipping } : {}),
          ...(isOzon && containersCount ? { containers_count: Number(containersCount) } : {}),
          ...(acknowledgeDeparted ? { acknowledge_departed: true } : {}),
        }),
      );
      const result = data?.session || data;
      const inBackground = !isLocalWarehouse && Boolean(result?.ms_background || result?.status === 'confirming');
      setNotice(inBackground
        ? (retry
          ? 'Повтор: отгрузки МойСклад идут в фоне'
          : 'WB готов. Отгрузки МойСклад идут в фоне — можно закрыть окно')
        : (retry
          ? `Повтор выполнен: ${result?.status || data.status}`
          : `Подтверждение: ${result?.status || data.status}`));
      setDepartedPrompt(null);
      setConfirmTarget(null);
      setShippingForm(null);
      setShippingError('');
      setContainersCount('');
      setDetail(result);
      if (inBackground && statusFilter === 'open') setStatusFilter('confirming');
      await load();
    } catch (requestError) {
      const data = requestError?.response?.data;
      if (data?.code === 'containers_count_required') {
        setContainersError(data.message || 'Укажите количество грузомест');
        await load();
      } else if (
        !acknowledgeDeparted
        && data?.code === 'orders_left_mp'
        && data?.details?.can_acknowledge
      ) {
        setDepartedPrompt({
          session,
          retry,
          orders: Array.isArray(data.details.departed_orders) ? data.details.departed_orders : [],
        });
      } else {
        setError(formatApiError(requestError));
      }
    } finally {
      setBusyId(null);
    }
  };

  const runPrintSupplyQr = async (session, supply) => {
    if (!session?.id || !supply?.id || !printerUid) return;
    setQrBusyId(supply.id);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/loading-sessions/${session.id}/supplies/${supply.id}/print-qr`,
        withAccountBody(marketplace, accountId, { printer_uid: printerUid }),
      );
      const result = data?.session || data;
      setNotice(`QR поставки ${supply.wb_supply_id} отправлен на печать`);
      setDetail(result);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setQrBusyId(null);
    }
  };

  const runCancel = async (session) => {
    setBusyId(session.id);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/loading-sessions/${session.id}/cancel`,
        withAccountBody(marketplace, accountId),
      );
      setNotice(`Сессия ${data?.session?.code || session.code} отменена${isLocalWarehouse ? '' : ', палеты возвращены в «закрыта»'}`);
      setCancelTarget(null);
      await reloadDetail(session.id);
      await load();
    } catch (requestError) {
      // 409 carriage_created / act_confirmed / supply_transferred — текст бэкенда.
      setError(formatApiError(requestError));
      setCancelTarget(null);
    } finally {
      setBusyId(null);
    }
  };

  const runRemovePallet = async ({ session, link }) => {
    const palletId = Number(link?.pallet_id || link?.pallet?.id || link?.id);
    if (!session?.id || !palletId) return;
    setRemoveBusy(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/loading-sessions/${session.id}/remove-pallet`,
        withAccountBody(marketplace, accountId, { pallet_id: palletId }),
      );
      setNotice(`Палета ${link?.pallet?.code || link?.code || palletId} снята с сессии`);
      setRemoveTarget(null);
      await reloadDetail(session.id);
      await load();
    } catch (requestError) {
      // 409 supply_transferred и прочие — показываем текст бэкенда.
      setError(formatApiError(requestError));
      setRemoveTarget(null);
    } finally {
      setRemoveBusy(false);
    }
  };

  const openAddPallets = async (session) => {
    if (!session?.id || session.status !== 'open') return;
    setAddPalletsOpen(true);
    setAddSelected(new Set());
    setAddError('');
    setAddLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/pallets`, {
        params: withAccountParams(marketplace, accountId, {
          status: 'closed',
          page: fulfillmentListPage(marketplace, 0),
          page_size: 100,
          sort_key: 'updated_at',
          sort_dir: 'desc',
        }),
      });
      const inSession = new Set(
        (session.pallets || [])
          .map((p) => Number(p.pallet_id || p.pallet?.id || p.id))
          .filter((id) => Number.isFinite(id) && id > 0),
      );
      const items = (data?.items || []).filter((p) => {
        const id = Number(p.id);
        if (!id || inSession.has(id)) return false;
        if (p.loading_session_id && Number(p.loading_session_id) !== Number(session.id)) return false;
        return true;
      });
      setAddCandidates(items);
    } catch (requestError) {
      setAddCandidates([]);
      setAddError(formatApiError(requestError));
    } finally {
      setAddLoading(false);
    }
  };

  const toggleAddPallet = (palletId) => {
    setAddSelected((prev) => {
      const next = new Set(prev);
      if (next.has(palletId)) next.delete(palletId);
      else next.add(palletId);
      return next;
    });
  };

  const submitAddPallets = async () => {
    if (!detail?.id || !addSelected.size) return;
    setAddBusy(true);
    setAddError('');
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/loading-sessions/${detail.id}/add-pallets`,
        withAccountBody(marketplace, accountId, {
          pallet_ids: [...addSelected],
        }),
      );
      const result = data?.session || data;
      const added = data?.add_results?.added || [];
      const rejected = data?.add_results?.rejected || [];
      const parts = [];
      if (added.length) parts.push(`добавлено ${added.length}`);
      if (rejected.length) parts.push(`отклонено ${rejected.length}`);
      setNotice(`Сессия ${result?.code || detail.code}: ${parts.join(', ') || 'готово'}`);
      setDetail(result);
      setAddSelected(new Set());
      await load();
      if (rejected.length) {
        const lines = rejected.slice(0, 8).map((r) => (
          `${r.code || r.pallet_id || '—'}: ${r.message || 'ошибка'}`
        ));
        setAddError(`${lines.join('\n')}${rejected.length > 8 ? `\n…и ещё ${rejected.length - 8}` : ''}`);
        await openAddPallets(result);
      } else {
        setAddPalletsOpen(false);
      }
    } catch (requestError) {
      setAddError(formatApiError(requestError));
    } finally {
      setAddBusy(false);
    }
  };

  const columns = COLUMNS.map((column) => (
    column.key === 'vehicle_label'
      ? { ...column, label: isYandex ? 'Склад' : isOzon ? 'Метод доставки' : 'Машина' }
      : column.key === 'stats'
        ? { ...column, label: isLocalWarehouse ? 'Палеты' : 'Палеты / заказы' }
        : column
  ));

  const actionButtons = (row, { compact = false } = {}) => {
    if (!canConfirm || !row) return null;
    const busy = busyId === row.id;
    const showConfirm = canConfirmSession(row, { isOzon, isYandex, isLocalWarehouse });
    const showRetry = !isLocalWarehouse && canRetrySession(row);
    const showCancel = canCancelSession(row, { isLocalWarehouse });
    if (!showConfirm && !showRetry && !showCancel) return null;

    return (
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        useFlexGap
        flexWrap="wrap"
        sx={{ maxWidth: compact ? 360 : 'none' }}
      >
        {showCancel && (
          <Button
            size="small"
            color="warning"
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={busy}
            onClick={() => setCancelTarget(row)}
          >
            Отменить
          </Button>
        )}
        {showRetry && (
          <Button
            size="small"
            color="warning"
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
            disabled={busy}
            onClick={() => setConfirmTarget({ session: row, retry: true })}
          >
            Повторить МС
          </Button>
        )}
        {showConfirm && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ConfirmIcon />}
            disabled={busy}
            onClick={() => setConfirmTarget({ session: row, retry: false })}
          >
            Подтвердить
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError('')}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {error}
        </Alert>
      )}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {!canConfirm && (
        <Alert severity="info">
          Доступен просмотр сессий и состава. Создание и подтверждение требуют права
          «АРМ кладовщика — управление» (fulfillmentExecute).
        </Alert>
      )}
      {!printerUid && canConfirm && !isYandex && (
        <Alert severity="warning">
          {isOzon
            ? 'Принтер не выбран — штрихкод carriage при подтверждении не отправится (остальное выполнится). Выберите принтер в Настройки → Печать.'
            : 'Принтер не выбран — QR поставок при подтверждении не отправятся (остальное выполнится). Выберите принтер в Настройки → Печать.'}
        </Alert>
      )}
      {isLocalWarehouse && !accountId && (
        <Alert severity="info">
          {isYandex
            ? 'Выберите кабинет Яндекса, чтобы увидеть сессии погрузки.'
            : 'Выберите кабинет Ozon, чтобы увидеть сессии погрузки.'}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={statusFilter}
            label="Статус"
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
          >
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canConfirm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={isLocalWarehouse && !accountId}
            >
              Новая сессия
            </Button>
          )}
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || (isLocalWarehouse && !accountId)}>
            Обновить
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align || 'left'}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={sort.key === column.key}
                        direction={sort.key === column.key ? sort.dir : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const busy = busyId === row.id;
                const errors = Number(row.stats?.errors || 0);
                const msPending = Number(row.stats?.ms_pending || 0);
                const palletCount = sessionPalletCount(row);
                const ordersCount = Number(row.stats?.orders || 0);
                const suppliesCount = Number(row.stats?.supplies || 0);
                return (
                  <TableRow key={row.id} hover selected={detail?.id === row.id}>
                    <TableCell sx={{ width: 44, pr: 0 }}>
                      <Tooltip title="Открыть состав сессии">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Открыть состав сессии"
                            onClick={() => openDetail(row)}
                            disabled={busy}
                          >
                            {busy ? <CircularProgress size={18} /> : <ViewIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="titleMedium" fontWeight={700}>{row.code}</Typography>
                      {row.last_error && (
                        <Typography variant="caption" color="error.main" component="p">
                          {row.last_error}
                        </Typography>
                      )}
                      {isOzon && row.delivery_method_id != null && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Метод: {row.delivery_method_id}
                        </Typography>
                      )}
                      {isYandex && row.warehouse_id != null && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Склад: {row.warehouse_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {palletCount} палет
                      </Typography>
                      {isYandex && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Грузомест: {ordersCount}
                        </Typography>
                      )}
                      {!isLocalWarehouse && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Заказов: {ordersCount}
                          {' · '}
                          Поставок: {suppliesCount}
                        </Typography>
                      )}
                      {row.ms_background && (
                        <Box sx={{ mt: 0.75, maxWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Отгрузки МС: {msProgressPercent(row)}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={msProgressPercent(row)}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      )}
                      {msPending > 0 && !row.ms_background && (
                        <Typography variant="caption" color="warning.main" component="p">
                          МС не закрыто: {msPending}
                        </Typography>
                      )}
                      {errors > 0 && (
                        <Typography variant="caption" color="error.main" component="p">
                          Ошибок: {errors}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {isYandex
                          ? (row.warehouse_id != null ? String(row.warehouse_id) : '—')
                          : isOzon
                            ? (row.delivery_method_id != null ? `DM ${row.delivery_method_id}` : '—')
                            : (row.vehicle_label || '—')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FulfillmentStatusChip kind="loadingSession" status={row.status} />
                      {errors > 0 && (
                        <Chip
                          size="small"
                          label={`Ошибки: ${errors}`}
                          sx={{ ...statusChipSx('failed'), mt: 0.5 }}
                        />
                      )}
                      {row.ms_background && (
                        <Chip
                          size="small"
                          color="info"
                          label="МС в фоне"
                          sx={{ mt: 0.5, ml: 0.5 }}
                        />
                      )}
                      {msPending > 0 && !row.ms_background && (
                        <Chip
                          size="small"
                          color="warning"
                          label={`МС: ${msPending}`}
                          sx={{ mt: 0.5, ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {actionButtons(row, { compact: true }) || (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                    <Stack spacing={1} alignItems="center">
                      <Typography>
                        {isLocalWarehouse && !accountId
                          ? (isYandex ? 'Выберите кабинет Яндекса' : 'Выберите кабинет Ozon')
                          : statusFilter
                            ? 'Нет сессий с выбранным статусом'
                            : 'Нет сессий погрузки'}
                      </Typography>
                      {canConfirm && !(isLocalWarehouse && !accountId) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setCreateOpen(true)}
                        >
                          Создать сессию
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_event, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            const value = Number(event.target.value);
            localStorage.setItem(PAGE_SIZE_KEY, String(value));
            setRowsPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Paper>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Сессия {detail?.code}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ whiteSpace: 'pre-line' }}>
                  {error}
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <FulfillmentStatusChip kind="loadingSession" status={detail.status} />
                {!isLocalWarehouse && detail.vehicle_label && (
                  <Chip size="small" variant="outlined" label={detail.vehicle_label} />
                )}
                {isOzon && detail.delivery_method_id != null && (
                  <Chip size="small" variant="outlined" label={`DM ${detail.delivery_method_id}`} />
                )}
                {isYandex && detail.warehouse_id != null && (
                  <Chip size="small" variant="outlined" label={`Склад ${detail.warehouse_id}`} />
                )}
                {isOzon && detail.carriage_id != null && (
                  <Chip size="small" variant="outlined" label={`Carriage ${detail.carriage_id}`} />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Палет: ${sessionPalletCount(detail)}`}
                />
                {isYandex && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Грузомест: ${Number(detail.stats?.orders || 0)}`}
                  />
                )}
                {!isLocalWarehouse && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Заказов: ${Number(detail.stats?.orders || 0)}`}
                  />
                )}
                {!isLocalWarehouse && detail.ms_background && (
                  <Chip size="small" color="info" label="Отгрузки МС в фоне" />
                )}
                {!isLocalWarehouse && Number(detail.stats?.ms_pending || 0) > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`МС не закрыто: ${detail.stats.ms_pending}`}
                  />
                )}
                {detail.last_error && (
                  <Alert severity="error" sx={{ py: 0, flex: 1 }}>{detail.last_error}</Alert>
                )}
              </Stack>

              {!isLocalWarehouse && detail.ms_background && (
                <Box>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    WB уже закрыт. Отгрузки МойСклад идут в фоне
                    ({msProgressPercent(detail)}% · осталось {Number(detail.stats?.ms_pending || 0)}).
                  </Alert>
                  <LinearProgress
                    variant="determinate"
                    value={msProgressPercent(detail)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )}
              {canConfirm && canConfirmSession(detail, { isOzon, isYandex, isLocalWarehouse }) && (
                <Alert severity="success">
                  {isOzon
                    ? 'Сессия готова к подтверждению: создание/approve carriage Ozon, акт и печать штрихкода.'
                    : isYandex
                      ? (writeStateDryRun
                        ? 'Сессия готова к подтверждению: палеты shipped, заказы Яндекса — «в доставке» (dry-run: First-mile API не вызывается).'
                        : 'Сессия готова к подтверждению: в Яндекс уйдёт число палет и акт first-mile, палеты — shipped, заказы — «в доставке».')
                      : 'Сессия готова к финальному подтверждению: создание финальных поставок WB, перенос заказов, deliver WB и печать QR. Отгрузки МойСклад пойдут в фоне.'}
                </Alert>
              )}
              {!isLocalWarehouse && canConfirm && canRetrySession(detail) && (
                <Alert severity="warning">
                  Есть незакрытые шаги. Повтор безопасно досоздаёт demand МС и не повторяет уже
                  успешные WB deliver/QR.
                </Alert>
              )}

              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Typography variant="titleSmall">Палеты</Typography>
                  {canConfirm && detail.status === 'open' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PalletAddIcon />}
                      disabled={busyId === detail.id || addBusy}
                      onClick={() => openAddPallets(detail)}
                    >
                      Добавить палеты
                    </Button>
                  )}
                </Stack>
                {!detail.pallets?.length && (
                  <Typography color="text.secondary">
                    Пока пусто — добавьте закрытые палеты здесь или сканом на ТСД.
                  </Typography>
                )}
                <Stack spacing={1}>
                  {(detail.pallets || []).map((p) => (
                    <Paper key={p.id || p.pallet_id} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography fontWeight={700}>{p.pallet?.code || p.code || p.pallet_id}</Typography>
                        {p.status && <FulfillmentStatusChip kind="sessionLink" status={p.status} />}
                        <FulfillmentStatusChip
                          kind="pallet"
                          status={p.pallet?.status || p.status}
                          shipmentError={p.pallet?.shipment_error}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {p.pallet?.items_count ?? p.items_count ?? 0} {isYandex ? 'грузомест' : 'шт.'}
                          {!isLocalWarehouse && p.account_id != null ? ` · кабинет ${p.account_id}` : ''}
                        </Typography>
                        {canConfirm && canRemovePallets(detail, { isLocalWarehouse }) && (
                          <Tooltip
                            title={palletSupply(detail, p)?.transfer_done
                              ? 'Поставка уже передана в WB'
                              : ''}
                          >
                            <span style={{ marginLeft: 'auto' }}>
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                startIcon={<PalletRemoveIcon />}
                                disabled={
                                  removeBusy
                                  || busyId === detail.id
                                  || Boolean(palletSupply(detail, p)?.transfer_done)
                                }
                                onClick={() => setRemoveTarget({ session: detail, link: p })}
                              >
                                Снять палету
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                      {p.pallet?.shipment_error && (
                        <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
                          Ошибка отгрузки: {p.pallet.shipment_error}
                        </Typography>
                      )}
                      {p.error_message && (
                        <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
                          {p.error_message}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
              {!isLocalWarehouse && (
                <>
                  <Box>
                    <Typography variant="titleSmall" sx={{ mb: 1 }}>Поставки</Typography>
                    {!(detail.supplies || []).length && (
                      <Typography color="text.secondary">
                        Группы поставок появятся после сканов палет; сами поставки WB создаются при подтверждении
                      </Typography>
                    )}
                    {(detail.supplies || []).map((s) => (
                      <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} useFlexGap flexWrap="wrap">
                        {s.wb_supply_id ? (
                          <Typography variant="bodySmall">{s.wb_supply_id}</Typography>
                        ) : (
                          <Typography variant="bodySmall" color="text.secondary">
                            Будет создана при подтверждении
                          </Typography>
                        )}
                        <FulfillmentStatusChip kind="sessionLink" status={s.status} />
                        {s.wb_supply_id && s.deliver_done && !s.qr_printed && (
                          <Tooltip title={printerUid ? '' : 'Выберите принтер в Настройки → Печать'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                disabled={!printerUid || qrBusyId === s.id}
                                onClick={() => runPrintSupplyQr(detail, s)}
                              >
                                {qrBusyId === s.id ? <CircularProgress size={16} /> : 'Печать QR'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {s.error_message && (
                          <Typography variant="caption" color="error">{s.error_message}</Typography>
                        )}
                        {s.qr_print_error && (
                          <Typography variant="caption" color="warning.main">
                            Печать QR: {s.qr_print_error}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Box>
                  <Box>
                    <Typography variant="titleSmall" sx={{ mb: 1 }}>Заказы / МС</Typography>
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                      <TableContainer sx={{ maxHeight: 320 }}>
                        <Table size="small" stickyHeader sx={TABLE_SX}>
                          <TableHead>
                            <TableRow>
                              <TableCell width={56}>Фото</TableCell>
                              <TableCell>WB</TableCell>
                              <TableCell>Артикул</TableCell>
                              <TableCell>МС demand</TableCell>
                              <TableCell>Отгружен</TableCell>
                              <TableCell>Ошибка</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(detail.orders || []).map((o) => (
                              <TableRow key={o.id} hover>
                                <TableCell>
                                  <FulfillmentProductThumb task={o.task} size={40} />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="bodyMedium" fontWeight={700}>{o.wb_order_id}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="bodyMedium" fontWeight={700}>
                                    {o.task?.article || o.task?.nm_id || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <FulfillmentStatusChip kind="msDemand" status={o.ms_demand_status} />
                                    {o.ms_demand_id && (
                                      <Typography variant="caption" color="text.secondary">
                                        {o.ms_demand_id}
                                      </Typography>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={o.shipped ? 'Да' : 'Нет'}
                                    sx={statusChipSx(o.shipped ? 'complete' : 'pending')}
                                  />
                                </TableCell>
                                <TableCell>
                                  {o.error_message || o.ms_demand_error ? (
                                    <Typography variant="caption" color="error">
                                      {o.error_message || o.ms_demand_error}
                                    </Typography>
                                  ) : o.ms_demand_warning ? (
                                    <Typography variant="caption" color="warning.main">
                                      {o.ms_demand_warning}
                                    </Typography>
                                  ) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                            {!(detail.orders || []).length && (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                  Заказов пока нет
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {canConfirm && detail && canCancelSession(detail, { isLocalWarehouse }) && (
            <Button
              color="warning"
              variant="outlined"
              disabled={busyId === detail.id}
              onClick={() => setCancelTarget(detail)}
            >
              Отменить сессию
            </Button>
          )}
          {!isLocalWarehouse && canConfirm && detail && canRetrySession(detail) && (
            <Button
              color="warning"
              variant="contained"
              disabled={busyId === detail.id}
              onClick={() => setConfirmTarget({ session: detail, retry: true })}
            >
              Повторить отгрузку МС
            </Button>
          )}
          {canConfirm && detail && canConfirmSession(detail, { isOzon, isYandex, isLocalWarehouse }) && (
            <Button
              color="primary"
              variant="contained"
              disabled={busyId === detail.id}
              onClick={() => setConfirmTarget({ session: detail, retry: false })}
            >
              Подтвердить погрузку
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addPalletsOpen}
        onClose={() => { if (!addBusy) setAddPalletsOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Добавить палеты в {detail?.code || 'сессию'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Выберите закрытые палеты без скана ШК. Отменённые на МП заказы блокируют добавление; уже ушедшие в доставку — нет.
            </Typography>
            {addError && (
              <Alert severity="error" onClose={() => setAddError('')} sx={{ whiteSpace: 'pre-line' }}>
                {addError}
              </Alert>
            )}
            {addLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!addLoading && !addCandidates.length && (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                Нет доступных закрытых палет для этого кабинета
              </Typography>
            )}
            {!addLoading && Boolean(addCandidates.length) && (
              <List dense disablePadding>
                {addCandidates.map((pallet) => {
                  const id = Number(pallet.id);
                  const checked = addSelected.has(id);
                  const secondary = [
                    pallet.warehouse_name || pallet.warehouse_id,
                    pallet.items_count != null
                      ? `${pallet.items_count} ${isYandex ? 'грузомест' : 'зак.'}`
                      : null,
                    isOzon && pallet.delivery_method_id != null
                      ? `DM ${pallet.delivery_method_id}`
                      : null,
                    !isLocalWarehouse && pallet.cargo_type != null ? `груз ${pallet.cargo_type}` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <ListItem key={id} disablePadding>
                      <ListItemButton
                        dense
                        disabled={addBusy}
                        onClick={() => toggleAddPallet(id)}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={pallet.code || `#${id}`}
                          secondary={secondary || undefined}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPalletsOpen(false)} disabled={addBusy}>Отмена</Button>
          <Button
            variant="contained"
            disabled={addBusy || !addSelected.size}
            startIcon={addBusy ? <CircularProgress size={16} color="inherit" /> : <PalletAddIcon />}
            onClick={submitAddPallets}
          >
            Добавить{addSelected.size ? ` (${addSelected.size})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Новая сессия погрузки</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="bodyMedium" color="text.secondary">
              {isOzon
                ? 'Создайте сессию здесь или на ТСД. Закрытые палеты можно добавить из АРМ без скана; метод доставки берётся из первой палеты.'
                : isYandex
                  ? 'Создайте сессию здесь или на ТСД. Закрытые палеты можно добавить из АРМ без скана; склад берётся из первой палеты.'
                  : 'Создайте сессию здесь или на ТСД. Закрытые палеты можно добавить из АРМ без скана или сканом на ТСД.'}
            </Typography>
            {!isLocalWarehouse && (
              <TextField
                label="Машина / рейс (необязательно)"
                value={vehicleLabel}
                onChange={(event) => setVehicleLabel(event.target.value)}
                fullWidth
                autoFocus
                disabled={creating}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Отмена</Button>
          <Button
            variant="contained"
            onClick={createSession}
            disabled={creating || (isLocalWarehouse && !accountId)}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmTarget)}
        onClose={closeConfirm}
        fullWidth
        maxWidth={isLocalWarehouse ? 'sm' : 'md'}
      >
        <DialogTitle>
          {confirmTarget?.retry ? 'Повтор подтверждения' : 'Финальное подтверждение погрузки'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isOzon
              ? (`Будут выполнены: создание/approve carriage Ozon, получение акта`
                + `${printerUid ? ' и печать штрихкода отгрузки' : ' (штрихкод без принтера не отправится)'}.`)
              : isYandex
                ? (writeStateDryRun
                  ? 'Палеты получат статус shipped, заказы Яндекса — «в доставке». First-mile API не вызывается.'
                  : 'Палеты получат статус shipped, заказы Яндекса — «в доставке». Будет вызван First-mile API: число палет и акт приёма-передачи.')
                : shippingForm?.skip_shipping
                ? 'WB уже закрыт. Повтор отправит только отгрузки МойСклад по заказам без demand / с ошибкой.'
                : confirmTarget?.retry
                ? 'Повтор: уже успешные шаги WB (создание поставки/перенос/deliver/QR) не выполняются снова. Если поставка ещё не закрыта — выберите пункт отгрузки. Отгрузки МойСклад пойдут в фоне.'
                : 'Сразу: создание финальных поставок в WB, перенос заказов, закрытие (deliver), печать QR'
                  + `${printerUid ? '' : ' (без принтера)'}. Отгрузки МойСклад по каждому заказу — в фоне, без ожидания в этом окне.`}
          </Typography>
          {isOzon ? (
            <TextField
              label="Количество грузомест"
              type="number"
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              value={containersCount}
              onChange={(event) => {
                setContainersCount(event.target.value.replace(/\D/g, ''));
                setContainersError('');
              }}
              inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
              error={Boolean(containersError)}
              helperText={containersError
                || 'Только для доверительной приёмки Ozon (коробки/мешки в отгрузке). Иначе оставьте пустым.'}
              disabled={busyId === confirmTarget?.session?.id}
            />
          ) : null}
          {!isLocalWarehouse ? (
            <WbSupplyShippingFields
              form={shippingForm}
              loading={shippingLoading}
              error={shippingError}
              onChange={setShippingForm}
              onCityChange={(city) => {
                const sessionId = confirmTarget?.session?.id;
                if (!sessionId) return;
                setShippingForm((prev) => (prev ? { ...prev, city } : prev));
                loadShippingOptions(sessionId, city, shippingForm);
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={busyId === confirmTarget?.session?.id}>Отмена</Button>
          <Button
            variant="contained"
            color={confirmTarget?.retry ? 'warning' : 'primary'}
            disabled={
              busyId === confirmTarget?.session?.id
              || (!isLocalWarehouse && (shippingLoading || !shippingFormReady(shippingForm)))
            }
            onClick={() => runConfirm(confirmTarget.session, confirmTarget.retry)}
          >
            {busyId === confirmTarget?.session?.id ? <CircularProgress size={20} /> : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(departedPrompt)}
        onClose={() => {
          if (busyId === departedPrompt?.session?.id) return;
          setDepartedPrompt(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {`Заказы уже выбыли на ${isOzon ? 'Ozon' : isYandex ? 'Яндексе' : 'WB'}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="warning">
              {`${departedPrompt?.orders?.length || 0} заказ(ов) уже выбыли на маркетплейсе — например, отгружены через личный кабинет. `}
              {isLocalWarehouse
                ? 'Отгрузить погрузку всё равно? Отменённые заказы нужно снять с палеты: с ними подтверждение не пройдёт.'
                : 'В новую поставку WB они не попадут. Сессия в Packer закроется, отгрузки МойСклад создадутся. Отменённые заказы нужно снять с палеты: с ними подтверждение не пройдёт.'}
            </Alert>
            <List dense sx={{ maxHeight: 280, overflow: 'auto', py: 0 }}>
              {(departedPrompt?.orders || []).map((row, index) => (
                <ListItem
                  key={`${row?.wb_order_id || row?.posting_number || row?.yandex_order_id || index}`}
                  disablePadding
                  sx={{ py: 0.25 }}
                >
                  <ListItemText
                    primary={departedOrderLine(row)}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDepartedPrompt(null)}
            disabled={busyId === departedPrompt?.session?.id}
          >
            Не отгружать
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busyId === departedPrompt?.session?.id}
            onClick={() => runConfirm(departedPrompt.session, departedPrompt.retry, { acknowledgeDeparted: true })}
          >
            {busyId === departedPrompt?.session?.id
              ? <CircularProgress size={20} />
              : 'Отгрузить всё равно'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="Отменить сессию погрузки?"
        message={isLocalWarehouse
          ? `Сессия ${cancelTarget?.code || ''} будет отменена, палеты вернутся в статус «закрыта».`
          : `Сессия ${cancelTarget?.code || ''} будет отменена, палеты вернутся в статус «закрыта». Если поставки в WB уже создавались, пустые будут удалены.`}
        confirmText="Отменить сессию"
        confirmColor="warning"
        loading={busyId === cancelTarget?.id}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => runCancel(cancelTarget)}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Снять палету с сессии?"
        message={`Палета ${removeTarget?.link?.pallet?.code || removeTarget?.link?.code || ''} вернётся в статус «закрыта»${isLocalWarehouse ? '' : '; если она была последней в уже созданной поставке WB, пустая поставка будет удалена'}.`}
        confirmText="Снять палету"
        confirmColor="warning"
        loading={removeBusy}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => runRemovePallet(removeTarget)}
      />
    </Stack>
  );
}
